import { useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useAppState } from "@hooks/index"
import { Formik, Form } from "formik"
import * as Yup from "yup"

import { formatPhoneNumber } from "../../helpers"

import Stack from "@mui/material/Stack"
import FormControl from "@mui/material/FormControl"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"

import { SignupBiodata } from "./signup-biodata"
import { SignupFeedback } from "./signup-feedback"
import { PageTitle } from "@components/page-title"
import { Loading } from "@components/loading"
import { ExternalPage } from "@components/external-page"

import { useAddProfileMutation, useVerifyEmailMutation } from "../auth-slice"
import { signInService } from "../auth-service"
import { fetchUser } from "@router/routes/protected-routes"
import { useSendWelcomeEmail } from "./use-send-welcome-email"

export const Signup = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const searchParams = new URLSearchParams(location?.search)
  const ref = searchParams.get("ref")
  const { saveFn } = useAppState()

  const [page, setPage] = useState(1)
  const [navigateError, setNavigateError] = useState("")
  const [submitting, setSubmitting] = useState(false)
  const submittingRef = useRef(false)
  const [addProfile, { isLoading }] = useAddProfileMutation()
  const [verifyEmail, { isLoading: loadingEmailVerify }] =
    useVerifyEmailMutation()
  const { sendWelcomeEmail } = useSendWelcomeEmail()

  const signupFn = async (val: any) => {
    if (submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    saveFn(val, true)
    const signupData = {
      ...val,
      phone: formatPhoneNumber(val.phone),
    }

    let signedIn = false
    try {
      const result: any = await addProfile(signupData)

      if (result?.data?.uid) {
        const { email, password, name } = signupData

        // send welcome email
        await sendWelcomeEmail({ name, email })
        const res: any = await signInService({ email, password })

        if (res?.uid) {
          await fetchUser()
          signedIn = true
          navigate("/dashboard", { replace: true })
        } else {
          throw new Error("Something went wrong, please refresh!")
        }
      } else {
        setNavigateError(result.data.error)
      }
    } catch (error) {
      setNavigateError(`${error}`)
      return error
    } finally {
      if (!signedIn) {
        submittingRef.current = false
        setSubmitting(false)
      }
    }
  }

  const handleNext = async (formik: any) => {
    const errors = await formik.validateForm()

    // verify profile
    try {
      if (formik.values?.email?.trim().slice(-3) === ".ru") {
        setNavigateError("Error! That action cannot be completed")
      } else {
        const result: any = await verifyEmail(formik.values)
        if (
          errors.name ||
          errors.password ||
          errors.email ||
          errors.phone ||
          result?.error
        ) {
          setNavigateError(
            result.data?.error ? result.data.error : "All values are required "
          )
        } else if (result?.data?.length) {
          setNavigateError("An account with that email already exist")
        } else {
          setNavigateError("")
          setPage(2)
        }
      }
    } catch (error) {
      setNavigateError(`${error}`)
      return error
    }
  }

  return (
    <ExternalPage>
      {page === 0 ? (
        <Loading />
      ) : (
        <Stack spacing={4}>
          <PageTitle>Sign up</PageTitle>
          <Formik
            initialValues={{
              name: "",
              email: "",
              password: "",
              phone: "",
              channel: "",
              referrer: ref || "",
            }}
            onSubmit={(values) => signupFn(values)}
            validationSchema={Yup.object({
              name: Yup.string()
                .min(3, "Name is too short")
                .required("Your name is required"),
              email: Yup.string()
                .email("Invalid email format")
                .required("Email address is required"),
              password: Yup.string()
                .min(6, "Password too short")
                .required("Password is required"),
              phone: Yup.string()
                .min(15, "Phone number is too short")
                .max(18, "Phone number is too long")
                .required("Your Whatsapp number is required"),
              channel: Yup.string().required("Required"),
              referrer: Yup.string().min(3, "Invalid referral code"),
            })}
          >
            {(formik) => (
              <Form onSubmit={formik.handleSubmit}>
                <FormControl fullWidth>
                  {page === 1 ? (
                    <SignupBiodata
                      formik={formik}
                      navigateError={navigateError}
                      isLoading={loadingEmailVerify}
                      handleNext={() => handleNext(formik)}
                    />
                  ) : null}
                  {page === 2 ? (
                    <SignupFeedback
                      formik={formik}
                      isLoading={isLoading || submitting}
                      navigateError={navigateError}
                    />
                  ) : null}
                </FormControl>
              </Form>
            )}
          </Formik>
          {page === 1 ? (
            <Stack spacing={1} alignItems={"center"}>
              <Typography>Already have an account?</Typography>
              <Button
                onClick={() => navigate("/")}
                color="primary"
                variant="outlined"
                fullWidth
              >
                Log in
              </Button>
            </Stack>
          ) : null}
          {page === 2 ? (
            <Button
              onClick={() => setPage(1)}
              color="primary"
              variant="outlined"
              fullWidth
            >
              Back
            </Button>
          ) : null}
        </Stack>
      )}
    </ExternalPage>
  )
}

export default Signup
